import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { db } from "../lib/instant";
import toast from "react-hot-toast";
import { Mail, User, Phone } from "lucide-react";

export default function Login({ theme }) {
  const primaryColor   = theme?.primaryColor   || "#6b2118";
  const accentColor    = theme?.accentColor    || "#c9a84c";
  const secondaryColor = theme?.secondaryColor || "#2e7d32";
  const navigate = useNavigate();

  const [mode, setMode]           = useState("login");
  const [loading, setLoading]     = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [magicCode, setMagicCode] = useState("");
  const [email, setEmail]         = useState("");
  const [name, setName]           = useState("");
  const [phone, setPhone]         = useState("");

  const handleSendMagic = async () => {
    if (!email.trim()) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try {
      await db.auth.sendMagicCode({ email: email.trim() });
      setMagicSent(true);
      toast.success("Magic code sent! Check your email.");
    } catch (err) {
      toast.error(err.message || "Failed to send code. Try again.");
    }
    setLoading(false);
  };

  const handleVerify = async () => {
    if (!magicCode.trim()) { toast.error("Please enter the code"); return; }
    setLoading(true);
    try {
      await db.auth.signInWithMagicCode({ email: email.trim(), code: magicCode.trim() });
      toast.success(mode === "register" ? "Account created! Welcome!" : "Welcome back!");
      navigate("/");
    } catch (err) {
      toast.error("Invalid or expired code. Please try again.");
    }
    setLoading(false);
  };

  const inp = {
    width: "100%", padding: "13px 14px 13px 44px",
    border: "2px solid #e8e0d8", borderRadius: 12,
    fontSize: "0.95rem", outline: "none", fontFamily: "inherit",
    background: "#fff", color: "#2c1810", boxSizing: "border-box", display: "block",
  };

  return (
    <div style={{
      paddingTop: 64, minHeight: "100vh",
      background: `linear-gradient(135deg, #2c0f0a 0%, ${primaryColor} 60%, #1a0a05 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 20px",
    }}>
      <div style={{
        background: "#fff", borderRadius: 24, padding: "40px 36px",
        width: "100%", maxWidth: 420, boxShadow: "0 24px 80px rgba(0,0,0,0.35)",
        boxSizing: "border-box",
      }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: "1.7rem", color: primaryColor, marginBottom: 4 }}>ACHU Health Mix</div>
          <div style={{ color: "#999", fontSize: "0.85rem" }}>
            {mode === "login" ? "Welcome back!" : "Create your account"}
          </div>
        </div>

        <div style={{ display: "flex", background: "#f5f0eb", borderRadius: 12, padding: 4, marginBottom: 28 }}>
          {[["login","Login"],["register","Register"]].map(([m,label]) => (
            <button key={m} onClick={() => { setMode(m); setMagicSent(false); setMagicCode(""); }} style={{
              flex: 1, padding: "10px", border: "none", borderRadius: 10,
              background: mode === m ? primaryColor : "transparent",
              color: mode === m ? "#fff" : "#888",
              fontWeight: 700, fontSize: "0.9rem", cursor: "pointer", transition: "all 0.2s",
            }}>{label}</button>
          ))}
        </div>

        {!magicSent && (
          <div>
            {mode === "register" && (
              <>
                <div style={{ position: "relative", marginBottom: 14 }}>
                  <User size={16} style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", color:"#bbb", pointerEvents:"none" }} />
                  <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Full name" style={inp} />
                </div>
                <div style={{ position: "relative", marginBottom: 14 }}>
                  <Phone size={16} style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", color:"#bbb", pointerEvents:"none" }} />
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone number" style={inp} />
                </div>
              </>
            )}
            <div style={{ position: "relative", marginBottom: 20 }}>
              <Mail size={16} style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", color:"#bbb", pointerEvents:"none" }} />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSendMagic()}
                placeholder="Email address" style={inp} />
            </div>
            <button onClick={handleSendMagic} disabled={loading} style={{
              width:"100%", padding:"14px", background: loading ? "#ccc" : primaryColor,
              color:"#fff", border:"none", borderRadius:12,
              fontWeight:700, fontSize:"1rem", cursor: loading ? "not-allowed" : "pointer", marginBottom:12,
            }}>{loading ? "Sending code..." : "Send Magic Code →"}</button>
            <div style={{ textAlign:"center", padding:"12px 16px", background:"#fdf6e3", borderRadius:10, fontSize:"0.82rem", color:"#888" }}>
              📧 We'll email you a 6-digit code. No password needed!
            </div>
          </div>
        )}

        {magicSent && (
          <div>
            <div style={{ background:"#e8f5e9", padding:"14px 16px", borderRadius:12, marginBottom:20, fontSize:"0.88rem", color:"#388e3c", lineHeight:1.6 }}>
              ✉️ Code sent to <strong>{email}</strong>.<br/>Check your inbox (and spam folder).
            </div>
            <label style={{ display:"block", fontSize:"0.8rem", fontWeight:700, color:"#555", marginBottom:8 }}>Enter 6-digit code</label>
            <input
              type="text" inputMode="numeric" maxLength={6}
              value={magicCode} onChange={e => setMagicCode(e.target.value.replace(/\D/g,""))}
              onKeyDown={e => e.key === "Enter" && handleVerify()}
              placeholder="000000"
              style={{
                width:"100%", padding:"16px 14px", border:`2px solid ${magicCode.length===6 ? primaryColor : "#e0e0e0"}`,
                borderRadius:12, fontSize:"2rem", letterSpacing:12, textAlign:"center", outline:"none",
                fontFamily:"monospace", fontWeight:700, color:primaryColor, background:"#fdf6e3",
                boxSizing:"border-box", display:"block", marginBottom:16, transition:"border-color 0.2s",
              }}
            />
            <button onClick={handleVerify} disabled={loading || magicCode.length < 6} style={{
              width:"100%", padding:"14px",
              background: loading || magicCode.length < 6 ? "#ccc" : secondaryColor,
              color:"#fff", border:"none", borderRadius:12,
              fontWeight:700, fontSize:"1rem",
              cursor: loading || magicCode.length < 6 ? "not-allowed" : "pointer", marginBottom:10,
            }}>{loading ? "Verifying..." : "✓ Verify & Sign In"}</button>
            <button onClick={() => { setMagicSent(false); handleSendMagic(); }} style={{
              width:"100%", background:"none", border:"none", color:"#888", fontSize:"0.85rem", cursor:"pointer", padding:"6px 0",
            }}>Didn't receive it? Resend code</button>
            <button onClick={() => { setMagicSent(false); setMagicCode(""); }} style={{
              width:"100%", background:"none", border:"none", color:"#bbb", fontSize:"0.82rem", cursor:"pointer", padding:"4px 0",
            }}>← Change email</button>
          </div>
        )}

        <div style={{ textAlign:"center", marginTop:24 }}>
          <Link to="/" style={{ color:primaryColor, textDecoration:"none", fontWeight:600, fontSize:"0.85rem" }}>← Back to Home</Link>
        </div>
      </div>
    </div>
  );
}
